import React, { useCallback, useEffect, useMemo } from "react";
import { StyleSheet } from "react-native";
import { BonusInput, SalaryDeductionType } from "../../../__generated__/graphql";
import Modal from "../../Modal";
import ScreenCard from "../../../screens/ScreenCard";
import SimpleHeader from "../../SimpleHeader";
import VStack from "../../../ui_atoms/VStack";
import { Controller, useForm } from "react-hook-form";
import TextField from "../../TextField";
import { useI18n } from "../../../hooks/i18n-hooks";
import { useTheme } from "../../../hooks/use-theme";
import Text from "../../Text";
import { formatCurrency, getAmountWithClientDeductions } from "../../../utils";
import { useChainInputFocus } from "../../../hooks/use-chain-input-focus";
import { useValidateFieldMinMax } from "../../../hooks/use-validate-field-min-max";
import MoneyField from "../../MoneyField";

export type CreateBonusFormData = {
    amount?: number;
    description?: string;
};

type BonusCreateModalProps = {
    onCreate: (bonus: BonusInput) => void;
    onClose: () => void;
    visible: boolean;
    initialData?: CreateBonusFormData;
    clientDeductions: SalaryDeductionType[];
};

const BonusCreateModal: React.FC<BonusCreateModalProps> = ({
    onClose,
    onCreate,
    visible,
    initialData,
    clientDeductions,
}) => {
    const styles = useStyles();
    const { fonts } = useTheme();
    const i18n = useI18n();

    const {
        control,
        handleSubmit,
        reset,
        formState: { isValid },
        setValue,
    } = useForm<CreateBonusFormData>({
        mode: "onChange",
        reValidateMode: "onChange",
        defaultValues: {
            amount: undefined,
            description: "",
        },
    });
    useEffect(() => {
        if (visible) {
            if (initialData) {
                setValue("amount", (initialData.amount || 0) / 100);
                setValue("description", initialData.description);
            } else {
                reset();
            }
        }
    }, [initialData, reset, setValue, visible]);

    const handleCreateBonus = useCallback(
        (formData: CreateBonusFormData) => {
            onCreate({
                amount: (formData.amount || 0) * 100,
                description: formData.description!,
            });
        },
        [onCreate],
    );

    const { chainInputFocus } = useChainInputFocus();
    const validateFieldMinMax = useValidateFieldMinMax();

    return (
        <Modal visible={visible} onClose={onClose} hideCloseButton>
            <SimpleHeader title={i18n.t("bonus.title")} onClose={onClose} mode="small" />
            <ScreenCard
                buttons={[
                    {
                        onPress: handleSubmit(handleCreateBonus),
                        mode: "secondary",
                        isValid: isValid,
                        title: !initialData ? i18n.t("add") : i18n.t("edit"),
                    },
                ]}
                withAssistanceButton
            >
                <VStack gap={1} h="2/3">
                    <Controller
                        name="amount"
                        control={control}
                        rules={{
                            required: true,
                            validate: {
                                minMax: (value) =>
                                    validateFieldMinMax({
                                        value,
                                        max: 1_000_000,
                                    }),
                            },
                        }}
                        render={({
                            field: { onChange, value, onBlur, name },
                            fieldState: { error },
                        }) => {
                            const amountInCents = value ? value * 100 : undefined;
                            return (
                                <VStack gap="1/3">
                                    <>
                                        <Text style={styles.amountTitle}>
                                            {i18n.t("bonus.amount_title")}
                                        </Text>
                                        <Text style={styles.amountSubtitle}>
                                            {i18n.t("bonus.amount_subtitle")}
                                        </Text>
                                    </>
                                    <MoneyField
                                        value={value}
                                        onChange={onChange}
                                        onBlur={onBlur}
                                        error={error?.message}
                                        inputStyle={fonts.headlineMedium}
                                        showErrorText
                                        {...chainInputFocus(name)}
                                    />
                                    <Text
                                        style={[
                                            styles.amountPreview,
                                            !amountInCents && { opacity: 0 },
                                        ]}
                                    >
                                        {i18n.t("bonus.amount_with_social_charges", {
                                            amount: formatCurrency({
                                                amountInCents: amountInCents
                                                    ? getAmountWithClientDeductions(
                                                          amountInCents,
                                                          clientDeductions,
                                                      )
                                                    : 0,
                                                displayDigitsWhenNoCents: false,
                                            }),
                                        })}
                                    </Text>
                                </VStack>
                            );
                        }}
                    />
                    <Controller
                        name="description"
                        control={control}
                        rules={{ required: true }}
                        render={({
                            field: { onChange, value, onBlur, name },
                            fieldState: { error },
                        }) => (
                            <TextField
                                label={i18n.t(`bonus.field_description`)}
                                value={value}
                                onChangeText={onChange}
                                onBlur={onBlur}
                                error={error?.message}
                                showErrorText
                                multiline
                                {...chainInputFocus(name)}
                            />
                        )}
                    />
                </VStack>
            </ScreenCard>
        </Modal>
    );
};

const useStyles = () => {
    const { fonts, colors } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                amountTitle: {
                    ...fonts.bodyMedium,
                },
                amountSubtitle: {
                    ...fonts.bodySmall,
                    color: colors.greyAccessible,
                },
                amountPreview: {
                    ...fonts.bodySmall,
                },
            }),
        [colors.greyAccessible, fonts.bodyMedium, fonts.bodySmall],
    );
};

export default BonusCreateModal;
